/**
 * Onboarding API Service
 * Handles organization creation and payment initialization for landing page onboarding
 * 
 * NOTE: Organization creation is now restricted to master admins only.
 * This onboarding flow should only be used if:
 * 1. The onboarding process is managed by master admins, OR
 * 2. The user being created is pre-approved as a master admin, OR
 * 3. This flow is part of a controlled signup process where organizations
 *    are created by master admins on behalf of new users.
 * 
 * The create_organization function will enforce this restriction at the database level.
 */

import { supabase } from './supabase'

/**
 * Create user account and submit organization request
 * The organization will be created after master admin approval
 */
export const createOrganizationAccount = async (onboardingData) => {
  try {
    const { organization, admin, subscription } = onboardingData

    // Step 1: Sign up the admin user
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: admin.email,
      password: admin.password,
      options: {
        data: {
          name: admin.name,
        },
      },
    })

    if (authError) throw authError

    // Wait for user to be created
    await new Promise((resolve) => setTimeout(resolve, 2000))

    // Step 2: Get the user ID
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User creation failed')
    }

    // Step 3: Submit organization request (instead of creating directly)
    // Master admin will review and approve the request
    const { data: requestData, error: requestError } = await supabase
      .from('organization_requests')
      .insert({
        user_id: user.id,
        user_email: admin.email,
        user_name: admin.name,
        requested_organization_name: organization.name,
        organization_type: organization.type || 'practice',
        organization_description: organization.description || null,
        contact_email: organization.email || admin.email,
        contact_name: admin.name,
        contact_phone: organization.phone || null,
        preferred_contact_method: 'email',
        // Store subscription preference for when request is approved
        additional_notes: `Requested subscription tier: ${subscription.tier}, Billing cycle: ${subscription.billingCycle}`,
        status: 'pending',
      })
      .select()
      .single()

    if (requestError) throw requestError

    return {
      success: true,
      data: {
        requestId: requestData.id,
        userId: user.id,
        status: 'pending',
        message: 'Your organization request has been submitted. Our team will review it and contact you soon.',
      },
    }
  } catch (error) {
    console.error('Error creating organization account:', error)
    return {
      success: false,
      error: error.message || 'Failed to create account',
    }
  }
}

/**
 * Initialize Paystack payment
 */
export const initializePayment = async (paymentData) => {
  try {
    const { organizationId, tier, billingCycle, email, amount } = paymentData

    // Get tier details
    const { data: tierData, error: tierError } = await supabase
      .from('subscription_tiers')
      .select('*')
      .eq('code', tier)
      .single()

    if (tierError) {
      // Fallback: use default pricing
      const defaultAmount = amount || (billingCycle === 'yearly' ? 1490 : 149)
    }

    // Create payment transaction
    const { data: transaction, error: transError } = await supabase
      .from('payment_transactions')
      .insert({
        organization_id: organizationId,
        amount: amount / 100, // Convert from kobo/cent to currency unit
        currency: 'NGN',
        payment_provider: 'paystack',
        status: 'pending',
      })
      .select()
      .single()

    if (transError) throw transError

    // Call Paystack via Supabase Edge Function
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
    const { data: { session } } = await supabase.auth.getSession()

    const response = await fetch(`${supabaseUrl}/functions/v1/paystack-payment/initialize`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || ''}`,
      },
      body: JSON.stringify({
        email,
        amount: amount, // Already in kobo/cent
        currency: 'NGN',
        reference: transaction.id,
        metadata: {
          organization_id: organizationId,
          tier: tier,
          billing_cycle: billingCycle,
          transaction_id: transaction.id,
        },
        callback_url: `${window.location.origin}/onboarding?action=verify`,
      }),
    })

    if (!response.ok) {
      throw new Error('Failed to initialize payment')
    }

    const paystackData = await response.json()

    // Update transaction
    await supabase
      .from('payment_transactions')
      .update({
        provider_transaction_id: paystackData.data?.reference,
        provider_reference: paystackData.data?.authorization_url,
      })
      .eq('id', transaction.id)

    return {
      success: true,
      data: {
        authorization_url: paystackData.data?.authorization_url,
        reference: paystackData.data?.reference,
      },
    }
  } catch (error) {
    console.error('Error initializing payment:', error)
    return {
      success: false,
      error: error.message || 'Failed to initialize payment',
    }
  }
}

